"use client"

import { useMounted } from "@mantine/hooks"
import type { ComponentProps, ReactNode } from "react"
import { NavLink } from "~/components/web/ui/nav-link"
import { useIsActive } from "~/hooks/use-is-active"

type WithActiveProps = {
  href: Parameters<typeof useIsActive>[0]
  exact?: boolean
  children: (isActive: boolean) => ReactNode
}

/**
 * Mount-gated active-route detection, shared by every "highlight the current
 * route" link. Renders `children(false)` on the server and the first client
 * pass — so it never reads the pathname during prerender and adds no hydration
 * mismatch — then re-renders with the real active state after mount.
 */
export const WithActive = ({ href, exact, children }: WithActiveProps) => {
  const mounted = useMounted()

  if (!mounted) return <>{children(false)}</>

  return (
    <WithActiveInner href={href} exact={exact}>
      {children}
    </WithActiveInner>
  )
}

const WithActiveInner = ({ href, exact, children }: WithActiveProps) => {
  return <>{children(useIsActive(href, exact))}</>
}

type ActiveNavLinkProps = ComponentProps<typeof NavLink> & { exact?: boolean }

export const ActiveNavLink = ({ exact, ...props }: ActiveNavLinkProps) => (
  <WithActive href={props.href} exact={exact}>
    {isActive => <NavLink {...props} isActive={isActive} />}
  </WithActive>
)
